import type { FaceLandmarker } from '@mediapipe/tasks-vision';
import type { LandmarkPoint } from '../types';

const WASM_ROOT = '/vendor/mediapipe/wasm';
const MODEL_PATH = '/vendor/mediapipe/face_landmarker.task';

let faceLandmarkerPromise: Promise<FaceLandmarker> | null = null;
let activeDelegate: 'GPU' | 'CPU' | null = null;

async function createFaceLandmarker(delegate: 'GPU' | 'CPU'): Promise<FaceLandmarker> {
  const { FaceLandmarker: FaceLandmarkerClass, FilesetResolver } = await import(
    '@mediapipe/tasks-vision'
  );
  const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);

  return FaceLandmarkerClass.createFromOptions(fileset, {
    baseOptions: {
      modelAssetPath: MODEL_PATH,
      delegate,
    },
    runningMode: 'IMAGE',
    numFaces: 2,
    minFaceDetectionConfidence: 0.55,
    minFacePresenceConfidence: 0.55,
    minTrackingConfidence: 0.5,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: false,
  });
}

async function initializeFaceLandmarker(): Promise<FaceLandmarker> {
  try {
    const gpuLandmarker = await createFaceLandmarker('GPU');
    activeDelegate = 'GPU';
    return gpuLandmarker;
  } catch (gpuError) {
    console.info('GPU face analysis is unavailable; using the CPU fallback.', gpuError);
    const cpuLandmarker = await createFaceLandmarker('CPU');
    activeDelegate = 'CPU';
    return cpuLandmarker;
  }
}

export function warmFaceLandmarker(): Promise<FaceLandmarker> {
  faceLandmarkerPromise ??= initializeFaceLandmarker().catch((error: unknown) => {
    faceLandmarkerPromise = null;
    activeDelegate = null;
    throw error;
  });
  return faceLandmarkerPromise;
}

export async function detectFaces(
  image: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement,
): Promise<LandmarkPoint[][]> {
  const landmarker = await warmFaceLandmarker();
  const result = landmarker.detect(image);

  return result.faceLandmarks.map((face) =>
    face.map((landmark) => ({
      x: landmark.x,
      y: landmark.y,
      z: landmark.z,
      visibility: landmark.visibility,
    })),
  );
}

export function getVisionDelegate(): 'GPU' | 'CPU' | null {
  return activeDelegate;
}

export async function disposeFaceLandmarker(): Promise<void> {
  const current = faceLandmarkerPromise;
  faceLandmarkerPromise = null;
  activeDelegate = null;
  if (!current) return;

  try {
    const landmarker = await current;
    landmarker.close();
  } catch {
    // Initialization already failed, so there is no active resource to close.
  }
}
